import type { CloudSaveMessageHandler } from '@/modules/cloud-saves'
import EventBus from '@/modules/event-bus'
import type { InGamePaymentsMessageHandler } from '@/modules/in-game-payments'
import type { GameAdHandlerConfig } from '@/modules/main-frame/ads/game-ad-handler.types'
import { PlatformService } from '@/modules/platform-service'
import type { SafeStorageMessageHandler } from '@/modules/safe-storage'
import { UserService } from '@/modules/user-service'
import type { GameEvent } from '@/types'
import { random } from '@/utils/helpers'

import type { GameAdHandler } from './ads/game-ad-handler'
import { MessageAction, type OutcomingMessage } from './message-types'

const VERSION = 1
const SOURCE = 'GameEvents'

function generateMessageId() {
    return `${Date.now()}-${random()}`
}

export type MainFrameOptions = {
    currentLanguage: string
    additionalPlatformInfo?: Record<string, unknown>
    isPaymentsSupported: boolean
    gameAdHandler: GameAdHandler
    storageMessageHandler: SafeStorageMessageHandler
    inGamePaymentsMessageHandler: InGamePaymentsMessageHandler
    cloudSaveMessageHandler: CloudSaveMessageHandler
    userService: UserService
    enableSync: boolean
    onLogin: () => void
    onGameStart: () => void
    onGameFocusChange: (isFocused: boolean) => void
}

const constructErrorMessage = (event: GameEvent, error: string) =>
    ({
        action: MessageAction.unknownMessageType,
        originalMessageEvent: event as GameEvent,
        payload: {
            error,
        },
        type: 'error',
    }) as OutcomingMessage

export class MainFrameService {
    private readonly boundMessageHandler
    private readonly boundGameAdHandlerCallback
    private readonly enableSync: boolean

    private gameAdHandler: GameAdHandler

    private userService: UserService

    private platformService: PlatformService

    private storageMessageHandler: SafeStorageMessageHandler

    private inGamePaymentsMessageHandler: InGamePaymentsMessageHandler

    private cloudSaveMessageHandler: CloudSaveMessageHandler

    private onLogin: () => void

    private onGameStart: () => void
    private onGameStartCalled = false

    private onGameFocusChange: (isFocused: boolean) => void

    private boundStickyRecalculateBanner?: (event: ScreenOrientationEventMap['change']) => void

    eventBus = new EventBus<Record<string, Record<string, unknown>>>()

    constructor(options: MainFrameOptions) {
        const {
            userService,
            storageMessageHandler,
            gameAdHandler,
            inGamePaymentsMessageHandler,
            cloudSaveMessageHandler,
            onLogin,
            onGameStart,
            onGameFocusChange,
            enableSync,
        } = options
        this.userService = userService
        this.enableSync = enableSync
        this.platformService = new PlatformService({
            currentLanguage: options.currentLanguage,
            additionalPlatformInfo: options.additionalPlatformInfo,
            isPaymentsSupported: options.isPaymentsSupported,
        })
        this.storageMessageHandler = storageMessageHandler
        this.inGamePaymentsMessageHandler = inGamePaymentsMessageHandler
        this.cloudSaveMessageHandler = cloudSaveMessageHandler
        this.onLogin = onLogin
        this.onGameStart = onGameStart
        this.onGameFocusChange = onGameFocusChange

        this.gameAdHandler = gameAdHandler
        this.boundGameAdHandlerCallback = this.gameAdHandlerCallback.bind(this)
        this.gameAdHandler.eventBus.addEventListener('adMessage', this.boundGameAdHandlerCallback)

        this.boundMessageHandler = this.messageHandler.bind(this)
        window.addEventListener('message', this.boundMessageHandler)
    }

    destroy() {
        window.removeEventListener('message', this.boundMessageHandler)
        this.gameAdHandler.eventBus.removeEventListener('adMessage', this.boundGameAdHandlerCallback)
        if (this.boundStickyRecalculateBanner) {
            window.screen.orientation.removeEventListener('change', this.boundStickyRecalculateBanner)
        }
    }

    messageHandler(event: MessageEvent) {
        if (event.data.source !== SOURCE || !event.source) {
            return
        }

        const { action, type } = event.data
        this.eventBus.dispatch(type, event.data)

        console.info(action, type)

        switch (type) {
            case 'liveness':
                if (!this.onGameStartCalled) {
                    this.onGameStart()
                    this.onGameStartCalled = true
                }

                this.sendMessage({
                    action: MessageAction.pong,
                    originalMessageEvent: event as GameEvent,
                    payload: { enableStorage: this.enableSync },
                })

                break

            case 'adv':
                this.gameAdHandler.handleMessage(event)
                break

            case 'storage':
                if (event.data.action === MessageAction.setItems) {
                    this.gameAdHandler.attemptMidrollOnCloudSave()
                }

                this.storageMessageHandler(event as GameEvent)
                    .then((msg) => {
                        if (msg) {
                            this.sendMessage(msg)
                        }
                    })
                    // No response on failure: the game gets a timeout error instead of wrong state
                    .catch(console.error)

                break
            case 'user':
                this.userService?.handleMessage(event as GameEvent, this.onLogin).then((msg) => {
                    if (msg) {
                        this.sendMessage(msg)
                    }
                })

                break
            case 'in-game-payments':
                this.inGamePaymentsMessageHandler(event as GameEvent).then((msg) => {
                    this.sendMessage(msg)
                })

                break
            case 'cloud-save':
                if (event.data.action === MessageAction.setCloudSaveState) {
                    this.gameAdHandler.attemptMidrollOnCloudSave()
                }

                this.cloudSaveMessageHandler(event as GameEvent)
                    .then((msg) => {
                        if (msg) {
                            this.sendMessage(msg)
                        }
                    })
                    // No response on failure: the game gets a timeout error instead of wrong state
                    .catch(console.error)

                break

            case 'platform':
                this.sendMessage(this.platformService.handleMessage(event as GameEvent))

                break

            case 'game':
                if (action === MessageAction.gameBlur) {
                    this.onGameFocusChange(false)
                }

                if (action === MessageAction.gameFocus) {
                    this.onGameFocusChange(true)
                }

                this.sendMessage({
                    action,
                    originalMessageEvent: event as GameEvent,
                })
                break

            default:
                this.sendMessage(constructErrorMessage(event as GameEvent, `Unknown message type "${type}"`))
        }
    }

    sendMessage({ action, originalMessageEvent, payload, type }: OutcomingMessage) {
        console.info(action, type, payload)
        const message = {
            action,
            id: generateMessageId(),
            payload: payload || {},
            responseToId: originalMessageEvent.data.id,
            source: SOURCE,
            type: type || originalMessageEvent.data.type,
            ver: VERSION,
        }
        originalMessageEvent.source?.postMessage(
            message,
            // @ts-ignore: Incorrect overload for postMessage in lib.dom.d.ts
            '*',
        )
        this.eventBus.dispatch(message.type, message)
    }

    gameAdHandlerCallback(event: CustomEvent<GameAdHandlerConfig['adMessage']>) {
        if (event.detail) {
            this.sendMessage(event.detail as OutcomingMessage)
        }
    }
}
